import qz from "qz-tray";
import moment from "moment";
import toCurrency from "@/helpers/toCurrency";
import { locationType } from "@/helpers/translateEnums";
import { addressFormatter, deliveryFee } from "@/helpers/textFormatter";
import { discountValue, productDiscount } from "@/helpers/discount";
import { subtotal, orderDiscount, total } from "@/helpers/order";

// faz a impressão
const print = (context, enums, order, company, branch, user, plain = true) => {
  const printData = generateRawPrintData(
    context,
    enums,
    order,
    company,
    branch,
    user
  );

  qz.printers
    .getDefault()
    .then((printer) => {
      const config = qz.configs.create(printer, {
        copies: Math.abs(branch.copiesPrinting),
      });

      qz.print(config, printData)
        .then(() => {
          console.log(`pedido ${order._id} enviado para a impressora`);
        })
        .catch((e) => {
          console.log(`problema na impressão do pedido ${order._id}`);
          console.log(e);
        });
    })
    .catch((e) => {
      console.log("erro ao capturar impressora");
      console.log(e);
    });
};

// remove todos os acentos
const normalize = (string) =>
  string.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// gera os dados pra impressão em modo não-html
const generateRawPrintData = (context, enums, order, company, branch, user) => {
  const { LocationTypes } = enums;
  const columns = branch.printerColumnSize || 40;
  const linebreak = "\x1B\x61\x30";
  const type = `Tipo: ${locationType(context, order.locationType)}`;
  const formattedAddress = addressFormatter(order.location);
  const takeAway = order.locationType === LocationTypes.TakeAway;
  const typeInfo = takeAway
    ? type + `\nHora da retirada: ${order.withdrawalTime}`
    : type;
  const _subtotal = subtotal(context, order);
  const _orderDiscount = orderDiscount(context, order);
  const _total = total(context, order) - _orderDiscount;
  const timeBr =
    new Date(order.createdAt).toLocaleDateString() +
    ` ` +
    new Date(order.createdAt).toLocaleTimeString();

  let data = [
    ` ${company.name} - ${branch.name}`,
    linebreak,
    `Informações do pedido`,
    `Pedido: ${order.seq}`,
    `Data: ${timeBr}`,
    typeInfo,
    linebreak,
    `Informações do cliente`,
    `Nome: ${order.noAuthName ? `${user.name} ( ${order.noAuthName} )` : user ? user.name : order.totemClientName}`,
  ];
  console.log(data);
  if (user && user.phone) data.push(`Telefone: ${order.noAuthPhone ? order.noAuthPhone : user ? user.phone : ""}`);
  if ((!order.noAuth && user && user.cpf) || order.cpfCustomer)
    data.push(`CPF: ${user ? user.cpf : order.cpfCustomer}`);

  if (user && user.cnpj) data.push(`CNPJ: ${user.cnpj}`);

  if (order.child.name) {
    data.push(linebreak);
    data.push(`Informações do aluno`);
    if (order.child.name) data.push(`Filho: ${order.child.name}`);
    if (order.child.grade) data.push(`Ano: ${order.child.grade}`);
    if (order.child.class) data.push(`Turma: ${order.child.class}`);
    if (order.child.shift) data.push(`Turno: ${order.child.shift}`);
  }

  if (formattedAddress) {
    data.push(linebreak);
    data.push(`Informações da entrega`);
    data.push(`Endereço: ${formattedAddress}`);

    if (order.location.address.complement)
      data.push(`Complemento: ${order.location.address.complement}`);

    if (order.location.address.reference)
      data.push(`Referência: ${order.location.address.reference}`);

    if (order.location.address.postalCode)
      data.push(`CEP: ${order.location.address.postalCode}`);
  }

  data.push(linebreak);

  data.push(`Itens`);

  data.push(generateTableRow("Qtd", "Item", "Preço", columns));

  order.items.forEach((item) => {
    data.push("-----------------------");

    data.push(
      generateTableRow(
        item.quantity,
        `${item.code ? `${item.code} - ` : ""}${item.title}`,
        toCurrency(item.originalPrice * item.quantity),
        columns
      )
    );

    if (item.complements && item.complements.length > 0) {
      data.push("Adicionais");

      let aux = new Set();

      item.complements.forEach((comp) => {
        aux.add(comp.complementGroup);
      });

      aux.forEach((a) => {
        item.complements.forEach((comp) => {
          if (comp.complementGroup === a) {
            let priceComplement = toCurrency(
              parseFloat(comp.price * comp.quantity * item.quantity).toFixed(2)
            );

            data.push(
              generateTableRow(
                comp.quantity,
                `${comp.code ? `${comp.code} - ` : ""}${comp.title}`,
                priceComplement,
                columns
              )
            );
          }
        });
      });
    }

    if (
      order.offers &&
      item.relatedOffer &&
      item.relatedOffer.discountType &&
      item.relatedOffer.discountType.type
    ) {
      const discounts = productDiscount(
        order.offers,
        item.relatedOffer.offerId
      );
      const discount = discounts.length ? discounts[0] : null;

      if (discount) {
        data.push("Descontos");

        data.push(
          generateTableRow(
            item.quantity,
            discount.title,
            `- ${toCurrency(
              discountValue(context, item.price, discount) * item.quantity
            )}`,
            columns
          )
        );
      }
    }

    if (item.note) {
      data.push(`obs: ${item.note}`);
    }
  });

  data.push(linebreak);

  data.push(`Informações de pagamento`);

  if (order.paymentMethod.label) {
    data.push(`Método: ${order.paymentMethod.label}`);
  }

  if (order.paymentMethod.legend) {
    data.push(order.paymentMethod.legend);
  }

  data.push(linebreak);

  data.push(`Subtotal: ${toCurrency(_subtotal)}`);

  if (
    order.deliveryFee ||
    (order.loyalty &&
      order.loyalty.rewards &&
      order.loyalty.rewards.deliveryFeeExemption) ||
    (order.coupon &&
      order.coupon.rewards &&
      order.coupon.rewards.deliveryFeeExemption)
  ) {
    data.push(`Taxa de entrega: ${deliveryFee(order)}`);
  }

  if (order.discount || order.loyalty || order.coupon) {
    if (order.loyalty) {
      data.push(
        `(${order.loyalty.title}): ${order.loyalty.triggers.pointsCost} pontos resgatados`
      );
    }

    if (order.coupon) {
      if (order.coupon && order.coupon.triggers.coupon) {
        data.push(`Cupom: ${order.coupon.triggers.coupon.code}`);
      }
      if (order.coupon.description) {
        data.push(`Descrição do desconto: ${order.coupon.description}`);
      }
    }

    if (order.discount) {
      data.push(`Desconto: - ${toCurrency(_orderDiscount)}`);
    }
  }

  data.push(`Total: ${toCurrency(_total)}`);

  if (order.change && order.change > 0) {
    data.push(`Pediu troco para: ${toCurrency(order.change)}`);
    data.push(`Levar troco de: ${toCurrency(order.change - _total)}`);
  }

  if (takeAway) {
    data.push(linebreak);

    data.push(`ATENÇÃO AOS PEDIDOS PARA RETIRADA`);

    if (order.paymentMethod.legend) {
      data.push(`De forma a evitar fraude, o cliente deve apresentar:`);
      data.push(` - documento de identificação original com foto`);
      data.push(` - o cartão utilizado na compra:`);
      data.push(`${order.paymentMethod.legend}`);
    }

    data.push(linebreak);
    data.push(linebreak);

    data.push(
      `No momento da retirada, o cliente deve assinar a linha tracejada abaixo:`
    );
    data.push(
      `Eu, ${user ? user.name : order.totemClientName
      }, confirmo que recebi em mãos os produtos referentes à essa compra.`
    );
    data.push(linebreak);
    data.push(linebreak);
    data.push(`__________________________________`);
    data.push(linebreak);
    data.push(linebreak);
    data.push(linebreak);
  }

  data.push("\x1B\x45\x0A");

  data.push(`Desenvolvido por klavi.com.br`);

  data.push(linebreak);
  data.push(linebreak);
  data.push(linebreak);
  data.push(linebreak);

  data = data.map((item) => normalize(item + "\n"));

  data.push("\x1B\x45\x0A");
  data.push("\n\n\n\n\n\n");
  data.push("\x1B\x69");
  data.push("\x10\x14\x01\x00\x05");

  return data;
};

// gera as linhas
const generateTableRow = (text1, text2, text3, printerColumnSize = 40) => {
  text1 = text1.toString();
  text2 = text2.toString();
  text3 = text3.toString();

  const firstColumn = printerColumnSize / 2 - 5;
  const secondColumn = printerColumnSize / 2 - 1;

  const s = (number, char) => {
    let result = "";

    for (let i = number - 1; i >= 0; i--) {
      result += char || " ";
    }

    return result;
  };

  const splitInLines = (string) => {
    return string.reduce(function (data, curr) {
      if (!data[0]) {
        data.push(curr);
        return data;
      }

      const text = data[data.length - 1].concat(["", curr]).replace(",", " ");

      if (text.length < secondColumn) {
        data[data.length - 1] = text;
        return data;
      } else {
        data.push(curr);
        return data;
      }
    }, []);
  };

  const line = s(printerColumnSize, "#");
  const text1regex = new RegExp("^.{" + text1.length + "}", "g");
  const text2regex = new RegExp("\\b#{" + (text2.length + 2) + "}", "g");
  const text3regex = new RegExp("#{" + text3.length + "}$", "g");
  let result;

  if (text2.length > firstColumn) {
    const splittedText = splitInLines(text2.split(" "));
    const text2regex = new RegExp(
      "\\b#{" + (splittedText[0].length + 2) + "}",
      "g"
    );

    result = line
      .replace(text1regex, text1)
      .replace(text2regex, "  " + splittedText[0])
      .replace(text3regex, text3)
      .replace(/#/g, " ");

    splittedText.forEach(function (text, index) {
      if (index === 0) return;

      let newLine = s(printerColumnSize, "#");
      const regex = new RegExp("^#{" + (text.length + 6) + "}", "g");

      if (index === splittedText.length - 1) {
        newLine = newLine.replace(regex, "     " + text);
      } else {
        newLine = newLine.replace(regex, "     " + text);
      }

      result += "\n" + newLine;
    });

    return result.replace(/#/g, " ");
  } else {
    result = line
      .replace(text1regex, text1)
      .replace(text2regex, "  " + text2)
      .replace(text3regex, text3)
      .replace(/#/g, " ");
  }

  return result;
};

const manualPrint = () => {
  const w = window.open();

  w.document.write(document.getElementById("printable-info").innerHTML);

  if(document.getElementById("printable-schedule")){
    w.document.write(`<p>Pedido Agendado</p>`);
    w.document.write(document.getElementById("printable-schedule").innerHTML);
  }

  if(document.getElementById("printable-payment")){
    w.document.write(document.getElementById("printable-payment").innerHTML);
  }

  w.document.write(document.getElementById("printable-table").innerHTML);
  
  w.document.write(`
  <br />
  <div>Desenvolvido por supermenu.com.br</div>
  `);

  console.log(w.document);

  w.print();

  w.close();
};

export { print, generateRawPrintData, manualPrint };
